import React from "react";
import { Textarea, Input, Box, Text } from "@chakra-ui/react";
import {
  QuizOptions,
  QuizSettings,
  PollSettings,
  PollOptions,
  SendOptions,
  SendSettings,
  SendTextArea,
} from "@components/ActivityTypes";
import { activityTypes, activityTitles } from "@constants/activities";
import { activitySchema } from "@schema/activities";
import ActivityStateButton from "../ActivityStateButton";
import activityInitialStateButton from "../../../assets/svg/activity-initial-state-button.svg";
import activityReadyStateButton from "../../../assets/svg/activity-ready-state-button.svg";
import activityPlayedStateButton from "../../../assets/svg/activity-played-state-button.svg";
import stopIcon from "@assets/svg/stop-activity-button.svg";
import sendRunningIcon from "@assets/svg/send_running.svg";

const activityPartsObject = {
  [activityTypes.quiz]: {
    options: QuizOptions,
    settings: QuizSettings,
    optionTooltipLabel: () => (
      <>
        <li>Minimum of 2 options</li>
        <li>Maximum of 5 options</li>
        <li>You must select at least one correct answer option</li>
      </>
    ),
    placeholder: "Ask a question",
    titleField: Textarea,
  },
  [activityTypes.poll]: {
    options: PollOptions,
    settings: PollSettings,
    optionTooltipLabel: () => (
      <>
        <li>Minimum of 2 options</li>
        <li>Maximum of 5 options</li>
      </>
    ),
    placeholder: "Ask a question",
    titleField: Textarea,
  },
  [activityTypes.share]: {
    options: SendOptions,
    settings: SendSettings,
    placeholder: "Type message to attendees here",
    titleField: SendTextArea,
  },
};

export const getActivityParts = (activityType) => ({
  ...activityPartsObject[activityType],
  titleField: activityPartsObject[activityType]?.titleField,
  schema: activitySchema(activityType).validation,
});

export const getLiveButtonProps = (
  activity,
  isAgendaLive,
  hasLiveActivity,
  startActivity,
  stopActivity,
  isGoingLive,
) => {
  const activityTitle = activityTitles[activity.type];

  // Activity is currently running/live
  if (
    isAgendaLive &&
    activity.live &&
    !activity.endTime &&
    activity.type !== activityTypes.share
  ) {
    return {
      icon: <ActivityStateButton src={stopIcon} />,
      disabled: false,
      "aria-label": `End ${activityTitle}`,
      onClick: stopActivity,
    };
  }

  // Share activity is currently running
  if (
    isAgendaLive &&
    activity.live &&
    !activity.endTime &&
    activity.type === activityTypes.share
  ) {
    return {
      icon: <ActivityStateButton src={sendRunningIcon} />,
      isSend: true,
      disabled: true,
      onClick: () => {},
    };
  }

  // Activity has been played/completed
  if (activity.endTime) {
    return {
      icon: <ActivityStateButton src={activityPlayedStateButton} />,
      disabled: true,
      "aria-label": `${activityTitle} completed`,
    };
  }

  // Agenda is live and activity can be played
  if (isAgendaLive && !activity.live && !activity.endTime) {
    return {
      icon: (
        <ActivityStateButton
          src={
            !(hasLiveActivity || isGoingLive) ||
            activity.type === activityTypes.share
              ? activityReadyStateButton
              : activityInitialStateButton
          }
        />
      ),
      disabled:
        (hasLiveActivity || isGoingLive) &&
        activity.type !== activityTypes.share,
      "aria-label":
        hasLiveActivity && activity.type !== activityTypes.share
          ? "Live activity running"
          : `Start ${activityTitle}`,
      onClick: startActivity,
    };
  }

  // Agenda is not live - initial state
  return {
    icon: <ActivityStateButton src={activityInitialStateButton} />,
    disabled: true,
    "aria-label": `Click Play to start ${activityTitle}`,
  };
};
